import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Box
} from '@material-ui/core';
import { getFirestore, doc, getDoc, updateDoc, collection, getDocs, query, where, orderBy, limit, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const defaultFeatureVideos = [
  {
    id: 'video1',
    url: 'https://www.youtube.com/embed/fw9ndEx8sHw',
    title: 'Coaching Session Example'
  },
  {
    id: 'video2',
    url: 'https://www.youtube.com/embed/eZCIrUrvbks',
    title: 'Client Success Story'
  },
  {
    id: 'video3',
    url: 'https://www.youtube.com/embed/b2HeQNuwzH0',
    title: 'Coaching Demonstration'
  },
  {
    id: 'video4',
    url: 'https://www.youtube.com/embed/a8_OeLEz5a4',
    title: 'Day Game is Hard At First, But Revolutionizes Your Dating Life (Testimonial)'
  },
  {
    id: 'video5',
    url: 'https://www.youtube.com/embed/3cm3K5R9dfY',
    title: 'Why Good Looking Guys Can Also Struggle with Girls'
  },
  {
    id: 'video6',
    url: 'https://www.youtube.com/embed/XzR-YPA1ca0',
    title: 'How to Approach Women in Public'
  },
  {
    id: 'video7',
    url: 'https://www.youtube.com/embed/DPpiFPWu8og',
    title: 'Coaching Session with Client'
  }
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    padding: '20px',
    marginTop: '20px'
  },
  section: {
    backgroundColor: 'rgba(217, 217, 217, 0.5)',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px'
  },
  sectionTitle: {
    color: 'white',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '20px'
  },
  formField: {
    marginBottom: '16px',
    '& .MuiInputBase-root': {
      color: 'white'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)'
      }
    }
  },
  saveButton: {
    background: 'linear-gradient(180deg, #f00 0%, #900 100%)',
    color: 'white',
    marginTop: '20px',
    '&:hover': {
      background: 'linear-gradient(180deg, #f00 0%, #800 100%)'
    }
  },
  divider: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '20px 0'
  },
  switchLabel: {
    color: 'white'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px'
  },
  videoItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  dragHandle: {
    cursor: 'grab',
    color: 'rgba(255, 255, 255, 0.5)'
  },
  deleteButton: {
    color: '#ff4444'
  },
  addButton: {
    marginTop: theme.spacing(2)
  },
  textField: {
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-input': {
      color: 'white'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)'
      }
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    }
  }
}));

export function SiteManagementPanel() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [emailTestData, setEmailTestData] = useState({
    destinationEmail: '',
    emailType: 'coursePurchase'
  });
  const [emailTestStatus, setEmailTestStatus] = useState({ loading: false, error: null, success: false });
  const [featureVideos, setFeatureVideos] = useState(defaultFeatureVideos);

  useEffect(() => {
    // Connect to functions emulator
    try {
      const functions = getFunctions();
      if (process.env.NODE_ENV === 'development') {
        connectFunctionsEmulator(functions, 'localhost', 5001);
        console.log('Connected to Firebase Functions emulator');
      }
    } catch (error) {
      console.error('Error connecting to Functions emulator:', error);
    }
    
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const db = getFirestore();
      const settingsDoc = await getDoc(doc(db, 'siteConfig', 'featureVideos'));
      
      if (settingsDoc.exists()) {
        setFeatureVideos(settingsDoc.data().videos);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading feature videos:', error);
      toast.error('Error loading feature videos');
      setLoading(false);
    }
  };

  const handleEmailTestInputChange = (e) => {
    setEmailTestData({
      ...emailTestData,
      [e.target.name]: e.target.value
    });
  };

  const handleTestEmail = async () => {
    setEmailTestStatus({ loading: true, error: null, success: false });
    try {
        console.log('Sending test email with data:', emailTestData);
        
        // Get the current user's ID token
        const auth = getAuth();
        if (!auth.currentUser) {
            throw new Error('You must be logged in to send test emails');
        }
        const idToken = await auth.currentUser.getIdToken();
        
        // Determine the base URL based on environment
        const baseUrl = process.env.NODE_ENV === 'development'
            ? 'http://localhost:5001/project-hector-6eaf1/us-central1'
            : 'https://us-central1-project-hector-6eaf1.cloudfunctions.net';
        
        console.log('Making request to:', `${baseUrl}/testEmail`);
        
        const response = await fetch(`${baseUrl}/testEmail`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify(emailTestData)
        });

        const data = await response.json();
        console.log('Response:', data);
        
        if (response.ok) {
            setEmailTestStatus({ loading: false, error: null, success: true });
        } else {
            throw new Error(data.error || 'Failed to send test email');
        }
    } catch (error) {
        console.error('Error sending test email:', error);
        setEmailTestStatus({ 
            loading: false, 
            error: error.message || 'Failed to send test email', 
            success: false 
        });
    }
  };

  const handleSave = async () => {
    try {
      const db = getFirestore();
      await setDoc(doc(db, 'siteConfig', 'featureVideos'), {
        videos: featureVideos
      });
      toast.success('Feature videos saved successfully');
    } catch (error) {
      console.error('Error saving feature videos:', error);
      toast.error('Error saving feature videos');
    }
  };

  const handleAddVideo = () => {
    const newVideo = {
      id: `video${Date.now()}`,
      url: '',
      title: ''
    };
    setFeatureVideos([...featureVideos, newVideo]);
  };

  const handleDeleteVideo = (index) => {
    const newVideos = [...featureVideos];
    newVideos.splice(index, 1);
    setFeatureVideos(newVideos);
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...featureVideos];
    newVideos[index] = {
      ...newVideos[index],
      [field]: value
    };
    setFeatureVideos(newVideos);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(featureVideos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFeatureVideos(items);
  };

  const handleReset = () => {
    const confirmReset = window.confirm(
      'Are you sure you want to reset all feature videos to defaults?'
    );
    
    if (confirmReset) {
      setFeatureVideos(defaultFeatureVideos);
      toast.success('Feature videos have been reset to defaults');
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Email Testing
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.formField}
              fullWidth
              label="Destination Email"
              name="destinationEmail"
              value={emailTestData.destinationEmail}
              onChange={handleEmailTestInputChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Email Type"
              name="emailType"
              value={emailTestData.emailType}
              onChange={handleEmailTestInputChange}
              variant="outlined"
              SelectProps={{
                native: true
              }}
            >
              <option value="coursePurchase">Course Purchase Email</option>
              <option value="paymentSuccess">Coaching Payment Success</option>
              <option value="coachingForm">Coaching Form Submission</option>
              <option value="contactForm">Support Form Submission</option>
            </TextField>
          </Grid>
        </Grid>
        <Button
          className={classes.saveButton}
          onClick={handleTestEmail}
          disabled={emailTestStatus.loading || !emailTestData.destinationEmail}
        >
          {emailTestStatus.loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Send Test Email'
          )}
        </Button>
        {emailTestStatus.error && (
          <Typography style={{ color: 'red', marginTop: '10px' }}>
            Error: {emailTestStatus.error}
          </Typography>
        )}
        {emailTestStatus.success && (
          <Typography style={{ color: 'green', marginTop: '10px' }}>
            Test email sent successfully!
          </Typography>
        )}
      </div>

      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Coaching Page Testimonials Videos
        </Typography>
        <Typography variant="body2" style={{ marginBottom: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
          Manage the videos shown on the coaching page testimonials section. Drag to reorder videos.
        </Typography>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="videos">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {featureVideos.map((video, index) => (
                  <Draggable key={video.id} draggableId={video.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={classes.videoItem}
                      >
                        <div {...provided.dragHandleProps} className={classes.dragHandle}>
                          <DragIndicatorIcon />
                        </div>
                        <Box flex={1}>
                          <TextField
                            className={classes.textField}
                            fullWidth
                            label="Video Title"
                            value={video.title}
                            onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                            variant="outlined"
                            margin="dense"
                          />
                          <TextField
                            className={classes.textField}
                            fullWidth
                            label="Video URL"
                            value={video.url}
                            onChange={(e) => handleVideoChange(index, 'url', e.target.value)}
                            variant="outlined"
                            margin="dense"
                            helperText="Enter YouTube embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)"
                          />
                        </Box>
                        <IconButton
                          className={classes.deleteButton}
                          onClick={() => handleDeleteVideo(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button
          variant="outlined"
          onClick={handleAddVideo}
          className={classes.addButton}
          style={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.23)' }}
        >
          Add Video
        </Button>
      </div>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          onClick={handleReset}
          style={{ backgroundColor: 'rgba(255, 0, 0, 0.2)' }}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </Box>
    </div>
  );
} 